import { CookieManager as Cookie } from '@cm-frontend/shared/cookie-manager';
import axios from 'axios';

import { UserStateLoadEvent } from '@/app-events';
import EventBus from '@/plugin/vue-event-bus';
import stateService from '@/services/state-service';
import store from '@/store/index';
import { getISOStringWithOffset, safeParse } from '@/utils/helpers';
import PageUtils from '@/utils/page-utils';

const SOLUTION_VUEX_KEY_MAP = {
  Solution: 'solution/setLastSolution',
  'Solution:next': 'solution/setNextPageSolution'
};

const afterSave = (res) => {
  if (res.data.success) {
    // save updated solution model to state
    store.commit('solution/setLastSolution', res.data.data);
  }
  return res.data;
};

const SECTIONS_PATH =
  'https://storage.googleapis.com/storage.codementum.com/studio/public/sections/';
const EVENT_SECTIONS_PATH =
  'https://storage.googleapis.com/events.codementum.com/';
const searchListEndpoints = [
  '/solutions',
  '/solutions/me',
  '/tutorialstates',
  '/tutorialstates/me',
  '/userstates'
];

function requestInterceptor(config) {
  const competitionName = store.getters['competition/competitionName'];
  if (
    !PageUtils.isStudioEvent() ||
    !competitionName ||
    competitionName === 'TEST_COMPETITION'
  ) {
    return config;
  }

  if (config.method === 'get') {
    const [path, paramsString] = config.url.split('?');

    const searchParams = new URLSearchParams(paramsString);
    if (searchListEndpoints.includes(path)) {
      const search = searchParams.get('search');
      if (search) {
        searchParams.set(
          'search',
          `${search} and eventName=='${competitionName}'`
        );
      } else {
        searchParams.set('search', `eventName=='${competitionName}'`);
      }
    } else {
      searchParams.set('eventName', competitionName);
    }
    config.url = config.url.split('?')[0] + '?' + searchParams.toString();
  }
  if (config.method === 'post') {
    config.data = config.data || {};
    config.data.eventName = competitionName;
  }

  return config;
}

class RemoteService {
  constructor() {
    this._api = null;
    this._panelApi = null;
    // this._competition = {};
    this.vendor = axios.create({
      baseURL: import.meta.env.VITE_VENDOR_BASE_URL
    });
  }

  // setCompetition(competition) {
  //   this._competition = competition;
  // }

  /*
   * Lazy initialize axios api to get authorization header in any case
   */
  get api() {
    if (this._api === null) {
      let baseURL;
      if (PageUtils.isStudioPrimary()) {
        baseURL = store.getters['CodementumUrl/studioApi'];
      } else if (PageUtils.isStudioSecondary()) {
        baseURL = store.getters['CodementumUrl/studio2Api'];
      } else if (PageUtils.isStudioEventPrimary()) {
        baseURL = store.getters['CodementumUrl/studioXApi']('studioevent')();
      } else if (PageUtils.isStudioEventSecondary()) {
        baseURL = store.getters['CodementumUrl/studioXApi']('studioevent2')();
      }

      this._api = axios.create({
        baseURL,
        headers: {
          Authorization: Cookie.getTokenString()
        }
      });

      this._api.interceptors.request.use(requestInterceptor);
    }

    return this._api;
  }

  get panelApi() {
    if (this._panelApi === null) {
      this._panelApi = axios.create({
        baseURL: store.getters['CodementumUrl/panelApi'],
        headers: {
          Authorization: Cookie.getTokenString()
        }
      });

      // @TODO: Not sure, ask about this
      // this.#panelApi.interceptors.request.use(requestInterceptor);
    }

    return this._panelApi;
  }

  loadSectionMetadata(challenge) {
    return this.api
      .get('/pages/number/' + challenge)
      .then((res) => res.data)
      .catch(async (error) => {
        if (error.response.data && error.response.data.errorCode === 2000) {
          let res = await this.api
            .get('tutorialstates/me?count=1&sort=currentPage,DESC')
            .then((res) => res.data);
          if (res.data.list && res.data.list.length > 0) {
            let currentPage = res.data.list[0].currentPage;
            let uuid = res.data.list[0].userUuid;
            let availableLastPageIndex = await this.getProducts(uuid);
            PageUtils.goToChallenge(
              availableLastPageIndex > currentPage
                ? currentPage
                : availableLastPageIndex
            );
          }
          return { success: false };
        }
        // means we don't privilage to this page
        if ([400, 404].includes(error.response.status)) {
          // go to last page of user
          PageUtils.goToChallenge();
        } else {
          if (error.errorCode === 2000) {
            console.log('Error 2000');
          }
          // unhandled error >:(
          console.error(error);
        }
        return { success: false };
      });
  }

  getTutorialId(challenge) {
    return this.api
      .get('/pages?search=pageNumber==' + challenge)
      .then((res) => {
        if (res.data.data.count > 0) {
          return res.data.data.list[0].tutorialId;
        }
        return 0;
      });
  }

  loadSections() {
    return this.api
      .get('/tutorials?count=1000&sort=index')
      .then((res) => res.data);
  }

  loadScenes() {
    return this.api
      .get('/pages?count=1000&sort=pageNumber')
      .then((res) => res.data);
  }

  loadSolutions() {
    return this.api
      .get('/solutions/me?count=1000&sort=page.pageNumber,DESC')
      .then((res) => {
        store.commit('solution/setSolutionCount', res.data.data.totalCount);

        return res.data;
      });
  }

  /**
   * Load section data by tutorialId from server. If the mode is local-sections then it fetchs from local
   * @param  {string}   tutorialId tutorial id
   * @return {promise}  http fetch promise
   */
  loadSectionData(index) {
    if (import.meta.env.VITE_MODE_LOCAL) {
      return this.loadFromLocal(index);
    }

    if (PageUtils.isStudioEvent()) {
      let baseUrl =
        import.meta.env.MODE === 'production'
          ? EVENT_SECTIONS_PATH
          : EVENT_SECTIONS_PATH.replace('codementum.com', 'codementum.co.uk');
      return axios
        .get(`${baseUrl}${PageUtils.getStudioType()}/section${index}.json`)
        .then((res) => res.data);
    }

    return axios
      .get(`${SECTIONS_PATH}${PageUtils.getStudioType()}/section${index}.json`)
      .then((res) => res.data);
  }

  /**
   * Load section data from local path
   * @param {string} tutorialId
   * @return {promise}  http fetch promise
   */
  loadFromLocal(tutorialIndex) {
    return this.vendor
      .get(`/sections/section${tutorialIndex}.json?v=${new Date().getTime()}`)
      .then((res) => res.data)
      .catch(() => {
        console.warn(`We couldn't find section${tutorialIndex}.json!`);
      });
  }

  /**
   * Load UserState and creates a new one if it doesn't exist
   * @return {promise}
   */
  loadUserState(fireEvent = true) {
    return this.api
      .get('/userstates/me')
      .then((res) => {
        if (res.data.success) {
          let userState = res.data.data;
          userState.params = safeParse(userState.params);
          userState.managementParams = safeParse(userState.managementParams, {
            extraTimeForEvent: []
          });
          stateService.saveUserState(userState);
          if (fireEvent) {
            EventBus.publish(new UserStateLoadEvent());
          }
        }
        return res.data;
      })
      .catch((error) => {
        // means we don't have any UserState yet
        if (error.response.status == 404) {
          return this.createUserState(fireEvent);
        } else {
          // unhandled error >:(
          console.error(error);
        }
      });
  }

  createUserState(fireEvent = true) {
    let userState = stateService.getUserState();
    return this.api
      .post('userstates', {
        params: JSON.stringify(userState.params)
      })
      .then((res) => {
        if (res.data.success) {
          let userState = res.data.data;
          userState.params = safeParse(userState.params);
          userState.managementParams = safeParse(userState.managementParams, {
            extraTimeForEvent: []
          });
          stateService.saveUserState(userState);
          if (fireEvent) {
            EventBus.publish(new UserStateLoadEvent());
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  getNextPage() {
    return this.api.get('/pages/nextPage').then((res) => {
      let response = {
        success: res.data.success,
        nextPage: res.data.data.next
      };

      if (PageUtils.isStudioEvent()) {
        // Daha oncesinde bu sekilde bir sey yapmisiz ama artik
        // nextPage in karar vermesine birakiyoruz.
        response.nextPage = PageUtils.getChallenge() + 1;

        // Bu kismi isEndOfStudioEvent fonksiyonunda
        // competition page count ve totalUserSolutionCount
        // ile kontrol ediyoruz.
        // if (PageUtils.isStudioEventPrimary()) {
        //   const pageCount = this._competition.eventPageCount || STUDIOEVENT_PAGE_COUNT;
        //   if (res.data.data.next >= pageCount) {
        //     response.nextPage = pageCount;
        //   }
        // }

        // if (PageUtils.isStudioEventSecondary()) {
        //   const pageCount = this._competition.event2PageCount || STUDIOEVENT2_PAGE_COUNT;
        //   if (res.data.data.next >= pageCount) {
        //     response.nextPage = pageCount;
        //   }
        // }
      }

      return response;
    });
  }

  loadPrincipalStats() {
    return this.api.get('/solutions/principal-stats').then((res) => res.data);
  }

  getProducts(uuid) {
    return this.panelApi.get(`/scope/${uuid}/products`).then((res) => {
      let type = PageUtils.getStudioType().toUpperCase();
      let product = res.data.data.products.find(
        (product) => product.productType === type
      );

      if (product && product.tutorials && product.tutorials.length) {
        return Math.max(
          ...product.tutorials.map((tutorial) => tutorial.pages).flat()
        );
      }

      return null;
    });
  }

  updateUserState(comp) {
    const userState = stateService.getUserState();
    let body = {
      params: JSON.stringify(userState.params)
    };
    if (typeof userState.submitted === 'boolean') {
      body.submitted = userState.submitted;
      if (userState.submitted) {
        let timeSpent = userState.params.eventTimers.reduce((acc, t) => {
          const ts = (typeof t.timeSpend === 'number' ? t.timeSpend : 0) / 1000;
          return acc + ts;
        }, 0);
        if (timeSpent) {
          body.timeSpent = timeSpent;
        }
      }
    }

    if (!userState.userStateId) {
      stateService.saveUserState(userState);
      return this.createUserState(false).then((res) => {
        if (comp && comp.setup && typeof comp.setup === 'function') {
          comp.setup();
        }

        return res.data;
      });
    }

    return this.api
      .patch('/userstates/' + userState.userStateId, body)
      .then((res) => {
        let userState = res.data.data;
        userState.params = safeParse(userState.params);
        userState.managementParams = safeParse(userState.managementParams, {
          extraTimeForEvent: []
        });
        stateService.saveUserState(userState);
        if (comp && comp.setup && typeof comp.setup === 'function') {
          comp.setup();
        }

        return res.data;
      })
      .catch((error) => {
        console.error(error);
        return { success: false };
      });
  }

  /**
   * Create TutorialState for the first time and next tutorials
   * @param  {number} section   tutorial id
   * @param  {number} challenge pageNumber
   * @return {promise}
   */
  createTutorialState(tutorialId, challenge) {
    // create new TutorialState for the principal user and section
    let tutorialState = {
      tutorialId: tutorialId,
      currentPage: challenge,
      completed: false,
      startedAt: getISOStringWithOffset(),
      numberOfAttempts: 0
    };

    stateService.saveTutorialState(tutorialState);

    return this.api.post('/tutorialstates', tutorialState).then((res) => {
      if (res.data.success) {
        stateService.removeTutorialState();
        stateService.saveTutorialState(res.data.data);
      }
      return res.data;
    });
  }

  /**
   * Load TutorialState by tutorial id.
   * If user doesn't have then it creates a new one for the given tutorial and page .
   *
   * @param  {number} tutorialId   tutorial id
   * @param  {number} challenge    scene number
   * @return {promise}
   */
  loadTutorialState(tutorialId, challenge) {
    return this.api
      .get('/tutorialstates/me?search=tutorial.id==' + tutorialId)
      .then((res) => {
        if (res.data.success) {
          if (res.data.data.count > 0) {
            stateService.removeTutorialState();
            stateService.saveTutorialState(res.data.data.list[0]);
            return res.data;
          } else {
            console.warn(
              'No tutorial state found for user, waiting to create a new one...'
            );
            return this.createTutorialState(tutorialId, challenge).then(
              (data) => {
                if (data.success) {
                  console.warn('TutorialState created for user.');
                }
                return data;
              }
            );
          }
        }
        return res.data;
      });
  }

  updateTutorialState(completed, challenge, rating, session) {
    if (stateService.hasTutorialState()) {
      let tutorialState = stateService.getTutorialState();

      if (completed === true) {
        tutorialState.finishedAt = session.endTime();
      } else {
        tutorialState.finishedAt = null;
      }
      // update only the most further page
      if (tutorialState.currentPage <= challenge && completed === false) {
        tutorialState.currentPage = challenge + 1;
      }

      tutorialState.completed = completed;
      // tutorialState.star and tutorialState.timeSpent are calculated on the API side
      // no need to track them here
      tutorialState.numberOfAttempts += session.get();

      stateService.saveTutorialState(tutorialState);

      return this.api
        .patch('/tutorialstates/' + tutorialState.tutorialStateId, {
          tutorialId: tutorialState.tutorialId,
          currentPage: tutorialState.currentPage,
          completed: tutorialState.completed,
          finishedAt: tutorialState.finishedAt,
          numberOfAttempts: tutorialState.numberOfAttempts
        })
        .then((res) => {
          if (res.data.success) {
            // tutorialState.star and tutorialState.timeSpent come calculated from API side
            stateService.removeTutorialState();
            stateService.saveTutorialState(res.data.data);
            // store.commit('setStars', res.data.data.star);
          }
        });
    }
  }

  /**
   * Load last TutorialState of user
   * @return {promise}
   */
  loadLastTutorialState() {
    return this.api
      .get('/tutorialstates/me?count=1&sort=currentPage,DESC')
      .then((res) => {
        if (res.data.success) {
          if (res.data.data.count > 0) {
            stateService.removeTutorialState();
            stateService.saveTutorialState(res.data.data.list[0]);
            return res.data;
          } else {
            console.warn(
              'No tutorial state found for user, waiting to create a new one...'
            );
            // create a tutorial state for the first challenge
            return this.getTutorialId(1).then((tutorialId) => {
              return this.createTutorialState(tutorialId, 1).then((data) => {
                if (data.success) {
                  console.warn('TutorialState created for user.');
                }
                return data;
              });
            });
          }
        }
        return res.data;
      });
  }

  /**
   * Fetch user solution and store in local storage
   * @param  {number} challenge pageNumber
   * @return {promise}          response.data
   */
  loadUserSolution(challenge, key = 'Solution') {
    store.commit(SOLUTION_VUEX_KEY_MAP[key], null);

    return this.api
      .get(`/solutions/me?search=page.pageNumber==${challenge}`)
      .then((res) => {
        if (res.data.success && res.data.data.count > 0) {
          store.commit(SOLUTION_VUEX_KEY_MAP[key], res.data.data.list[0]);
        }
        return res.data;
      });
  }

  /**
   * Create or update user solution for challenge
   * @param  {number}  challenge pageNumber
   * @param  {number}  rating    star count
   * @param  {enum}    mode      'python' or 'javascript'
   * @param  {string}  solution  solution code
   * @param  {Session} session   Session to track elapsed time
   * @return {promise}           either returns promise or undefined
   */
  createUserSolution(
    challenge,
    rating,
    mode,
    solution,
    session,
    failed = false
  ) {
    let model = store.getters.lastSolution;
    if (model) {
      return this.updateUserSolution(
        model,
        rating,
        mode,
        solution,
        session,
        failed
      );
    }

    return this.loadSectionMetadata(challenge).then((data) => {
      if (data.success) {
        let pageId = data.data.pageId;
        return this.api
          .post('/solutions/', {
            pageId,
            failed,
            star: rating,
            type: mode.toUpperCase(),
            timeSpent: session.time(),
            startedAt: session.startTime(),
            finishedAt: session.endTime(),
            solution: JSON.stringify(solution)
          })
          .then(afterSave)
          .then((data) => {
            store.commit('solution/incrementSolutionCount');
            store.commit('incrementStarsBy', data.data.star);
          })
          .catch(async ({ response }) => {
            const { data } = response;
            if (!data.success && data.errorCode === 2450) {
              let userSolution = await this.loadUserSolution(challenge);
              const currentSolution = userSolution.data.list[0];
              if (currentSolution) {
                return this.updateUserSolution(
                  currentSolution,
                  rating,
                  mode,
                  solution,
                  session,
                  failed
                );
              }
            }
          });
      }
    });
  }

  updateUserSolution(model, rating, mode, solution, session, failed = false) {
    /**
     * Do not update solution if
     *
     * - rating < old solution's stars
     * - solutions are same language and identical
     */
    if (rating < model.star) return;

    if (
      !model.failed &&
      !failed &&
      model.type === mode.toUpperCase() &&
      JSON.stringify(solution) === model.solution
    ) {
      return;
    }

    return this.api
      .patch('/solutions/' + model.solutionId, {
        failed,
        star: rating,
        type: mode.toUpperCase(),
        timeSpent: session.time(),
        finishedAt: session.endTime(),
        solution: JSON.stringify(solution)
      })
      .then(afterSave)
      .then((data) => {
        store.commit('incrementStarsBy', data.data.star - model.star);
      });
  }

  // createWrongUserSolution() {}
}

export default new RemoteService();
